<template>
  <s-modal
    v-model:open="showModal"
    :title="title || t('form.unsavedChanges')"
    :message="message || t('form.pageUnsavedChangesConfirmation')"
    :confirm="{
      label: confirm?.label || t('form.discardChanges'),
      color: confirm?.color || 'red',
      icon: confirm?.icon || 'trash-can',
    }"
    :cancel="{label: t('cancel'), color: 'gray'}"
    @confirm="handleConfirm"
    @cancel="handleCancel"
  />
</template>

<script setup lang="ts">
import {ref, onUnmounted} from 'vue';
import {router} from '@inertiajs/vue3';
import {useI18n} from 'vue-i18n';
import SModal, {ModalAction} from '../design-system/SModal.vue';

/**
 * Confirms if the user wants to leave the page when something is dirty.
 *
 * ### Actions that trigger the confirmation:
 * - Clicking on a link (inertia or external)
 * - Refreshing the page
 * - Entering a new URL in the address bar
 * - Closing the tab or the browser
 *
 * ### Actions that DO NOT trigger the confirmation:
 * There are several limitations due to the way Inertia's `router.on('before')` works.
 * - Submitting a form
 * - Pressing the back or forward buttons in the browser
 * - Pressing CMD/CTRL + [  or CMD/CTRL + ] to navigate through the history
 */
const {isDirty, message, title, confirm} = defineProps<{
  isDirty: () => boolean;
  message?: string;
  title?: string;
  confirm?: ModalAction;
}>();

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});

const showModal = ref(false);
const pendingNavigation = ref<{url: string; method: string} | null>(null);
const isNavigating = ref(false);

const beforeUnload = (e: BeforeUnloadEvent) => {
  if (isDirty() && !isNavigating.value) {
    // Note: Setting returnValue is deprecated but still required for browser compatibility
    // Modern browsers standardized on showing a generic message regardless of this value
    e.returnValue = message;
    return message;
  }
};

window.addEventListener('beforeunload', beforeUnload);

// Handle Inertia navigation
const removeListener = router.on('before', (event) => {
  const method = event.detail.visit.method.toLowerCase();
  const isFormSubmission = ['post', 'put', 'patch', 'delete'].includes(method);

  if (!isFormSubmission && isDirty() && !isNavigating.value) {
    event.preventDefault();
    pendingNavigation.value = {
      url: event.detail.visit.url.toString(),
      method: event.detail.visit.method,
    };
    showModal.value = true;
  }
});

const handleConfirm = () => {
  if (pendingNavigation.value) {
    const {url, method} = pendingNavigation.value;
    showModal.value = false;
    pendingNavigation.value = null;
    isNavigating.value = true;

    // Small delay to ensure modal is closed before navigation
    setTimeout(() => {
      router.visit(url, {
        method: method as any,
      });
    }, 0);
  }
};

const handleCancel = () => {
  showModal.value = false;
  pendingNavigation.value = null;
};

onUnmounted(() => {
  window.removeEventListener('beforeunload', beforeUnload);
  removeListener();
});
</script>

<i18n>
{
  "en": {
    "cancel": "Stay"
  },
  "fr": {
    "cancel": "Rester"
  }
}
</i18n>
